// src/utils/SessionPersistenceManager.ts
import { Message } from '../types/Message';

interface ConversationSession {
  sessionId: string;
  userId: string;
  messages: Message[];
  metadata: {
    startTime: number;
    lastActivity: number;
    deviceType: 'desktop' | 'mobile' | 'tablet';
    userAgent: string;
    title?: string;
  };
  state: {
    isStreamingResponse: boolean;
    activeProcesses: Record<string, boolean>;
    inputMode: 'text' | 'voice';
    voiceState: any;
  };
}

export class SessionPersistenceManager {
  private readonly storageKey = 'emcog_sessions';
  private readonly currentSessionKey = 'emcog_current_session';
  private readonly deletedSessionsKey = 'emcog_deleted_sessions'; // 🔥 Track deleted sessions
  private readonly maxSessions = 5;
  private readonly maxMessagesPerSession = 500;
  private readonly sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours

  private currentSession: ConversationSession | null = null;
  private autoSaveInterval: number | null = null;
  private syncInterval: number | null = null;
  private webSocketSend: ((message: any) => void) | null = null;
  private pendingServerSync: boolean = false;
  private deletedSessionIds: Set<string> = new Set(); // 🔥 In-memory cache of deleted IDs

  constructor() {
    this.loadDeletedSessions(); // 🔥 Load deleted session IDs first
    this.startAutoSave();
    this.startCrossDeviceSync();
    this.cleanupOldSessions();
  }

  /**
   * 🔥 Load deleted session IDs from localStorage
   */
  private loadDeletedSessions(): void {
    try {
      const data = localStorage.getItem(this.deletedSessionsKey);
      if (data) {
        const ids = JSON.parse(data) as string[];
        this.deletedSessionIds = new Set(ids);
        console.log(`📋 Loaded ${this.deletedSessionIds.size} deleted session IDs`);
      }
    } catch (error) {
      console.error('Failed to load deleted sessions:', error);
    }
  }

  /**
   * 🔥 Save deleted session IDs to localStorage
   */
  private saveDeletedSessions(): void {
    try {
      const ids = Array.from(this.deletedSessionIds);
      localStorage.setItem(this.deletedSessionsKey, JSON.stringify(ids));
    } catch (error) {
      console.error('Failed to save deleted sessions:', error);
    }
  }

  /**
   * 🔥 Mark a session as confirmed deleted by server (remove from pending deletes after some time)
   */
  public confirmServerDeletion(sessionId: string): void {
    // Keep in deleted set for a while to prevent re-merge on reconnect
    // Will be cleaned up after 1 hour
    setTimeout(() => {
      this.deletedSessionIds.delete(sessionId);
      this.saveDeletedSessions();
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * 🔥 NEW: Set WebSocket send function for server sync
   */
  public setWebSocketSend(sendFn: (message: any) => void): void {
    this.webSocketSend = sendFn;
    // Request sessions from server on connect
    this.requestSessionsFromServer();
  }

  /**
   * 🔥 NEW: Request sessions from server
   */
  public requestSessionsFromServer(): void {
    if (this.webSocketSend) {
      this.webSocketSend({
        type: 'get_sessions',
        userId: 'default'
      });
      console.log('📡 Requested sessions from server');
    }
  }

  /**
   * 🔥 NEW: Handle sessions received from server
   */
  public handleServerSessions(sessions: ConversationSession[]): void {
    console.log(`📡 Received ${sessions.length} sessions from server`);

    // 🔥 Filter out any sessions that were deleted locally
    const filteredServerSessions = sessions.filter(s => !this.deletedSessionIds.has(s.sessionId));
    if (filteredServerSessions.length !== sessions.length) {
      console.log(`📡 Filtered out ${sessions.length - filteredServerSessions.length} locally-deleted sessions`);
    }

    // Merge server sessions with local sessions
    const localSessions = this.getAllSessions();

    // Create a map of all sessions by ID
    const sessionMap = new Map<string, ConversationSession>();

    // Add local sessions (excluding deleted ones)
    for (const session of localSessions) {
      if (!this.deletedSessionIds.has(session.sessionId)) {
        sessionMap.set(session.sessionId, session);
      }
    }

    // Merge server sessions (server wins for conflicts based on lastActivity)
    for (const serverSession of filteredServerSessions) {
      const localSession = sessionMap.get(serverSession.sessionId);
      if (!localSession ||
          (serverSession.metadata.lastActivity > localSession.metadata.lastActivity)) {
        sessionMap.set(serverSession.sessionId, serverSession);
      }
    }

    // Save merged sessions to localStorage
    const mergedSessions = Array.from(sessionMap.values())
      .sort((a, b) => b.metadata.lastActivity - a.metadata.lastActivity)
      .slice(0, this.maxSessions);

    localStorage.setItem(this.storageKey, JSON.stringify(mergedSessions));
    console.log(`📡 Merged ${mergedSessions.length} sessions to local storage`);
  }

  /**
   * 🔥 NEW: Sync current session to server
   */
  public syncToServer(): void {
    if (this.webSocketSend && this.currentSession) {
      this.webSocketSend({
        type: 'save_session',
        session: this.currentSession
      });
      console.log('📡 Synced session to server:', this.currentSession.sessionId);
    }
  }

  /**
   * Initialize or restore a session
   */
  public initializeSession(userId: string): ConversationSession {
    // Try to restore existing session first
    const existingSession = this.getActiveSession(userId);
    
    if (existingSession && this.isSessionValid(existingSession)) {
      console.log('📱 Restoring existing session:', existingSession.sessionId);
      this.currentSession = existingSession;
      return existingSession;
    }

    // Create new session
    const sessionId = this.generateSessionId();
    const newSession: ConversationSession = {
      sessionId,
      userId,
      messages: [],
      metadata: {
        startTime: Date.now(),
        lastActivity: Date.now(),
        deviceType: this.detectDeviceType(),
        userAgent: navigator.userAgent,
        title: `Conversation ${new Date().toLocaleDateString()}`
      },
      state: {
        isStreamingResponse: false,
        activeProcesses: {},
        inputMode: 'text',
        voiceState: {}
      }
    };

    console.log('🆕 Creating new session:', sessionId);
    this.currentSession = newSession;
    this.saveCurrentSession();
    
    return newSession;
  }

  /**
   * Save current session state
   */
  public saveCurrentSession(): void {
    if (!this.currentSession) return;

    this.currentSession.metadata.lastActivity = Date.now();

    try {
      // Save to localStorage
      localStorage.setItem(this.currentSessionKey, JSON.stringify(this.currentSession));

      // Save to session list
      this.saveToSessionList(this.currentSession);

      // 🔥 NEW: Mark for server sync (done periodically, not on every save)
      this.pendingServerSync = true;

      console.log('💾 Session saved:', this.currentSession.sessionId);
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  }

  /**
   * Add message to current session
   * Auto-starts a new conversation if message limit is reached
   */
  public addMessage(message: Message): { newSessionStarted: boolean; session: ConversationSession } {
    if (!this.currentSession) {
      console.warn('No active session to add message to');
      return { newSessionStarted: false, session: this.initializeSession('default') };
    }

    // Check if we need to start a new conversation due to message limit
    if (this.currentSession.messages.length >= this.maxMessagesPerSession) {
      console.log(`📝 Message limit reached (${this.maxMessagesPerSession}), starting new conversation`);
      const userId = this.currentSession.userId;
      this.saveCurrentSession(); // Save current session before creating new one
      const newSession = this.createNewConversation(userId);
      newSession.messages.push(message);
      this.saveCurrentSession();
      return { newSessionStarted: true, session: newSession };
    }

    this.currentSession.messages.push(message);
    this.saveCurrentSession();
    return { newSessionStarted: false, session: this.currentSession };
  }

  /**
   * Update session state
   */
  public updateSessionState(stateUpdate: Partial<ConversationSession['state']>): void {
    if (!this.currentSession) return;

    this.currentSession.state = {
      ...this.currentSession.state,
      ...stateUpdate
    };
    
    this.saveCurrentSession();
  }

  /**
   * Get current session
   */
  public getCurrentSession(): ConversationSession | null {
    return this.currentSession;
  }

  /**
   * Get all sessions for a user
   */
  public getUserSessions(userId: string): ConversationSession[] {
    try {
      const sessions = this.getAllSessions();
      return sessions
        .filter(session => session.userId === userId)
        .sort((a, b) => b.metadata.lastActivity - a.metadata.lastActivity);
    } catch (error) {
      console.error('Failed to get user sessions:', error);
      return [];
    }
  }

  /**
   * Switch to a different session
   */
  public switchToSession(sessionId: string): ConversationSession | null {
    try {
      const sessions = this.getAllSessions();
      const session = sessions.find(s => s.sessionId === sessionId);
      
      if (session) {
        this.currentSession = session;
        localStorage.setItem(this.currentSessionKey, JSON.stringify(session));
        console.log('🔄 Switched to session:', sessionId);
        return session;
      }
      
      console.warn('Session not found:', sessionId);
      return null;
    } catch (error) {
      console.error('Failed to switch session:', error);
      return null;
    }
  }

  /**
   * Create a new conversation thread
   */
  public createNewConversation(userId: string): ConversationSession {
    // Save current session if it exists
    if (this.currentSession) {
      this.saveCurrentSession();
    }

    // Create new session
    return this.initializeSession(userId);
  }

  /**
   * Cross-device session synchronization
   * 🔥 FIXED: Actually syncs to server now!
   */
  public syncSessions(): void {
    if (!this.currentSession) return;

    try {
      // Generate sync data
      const syncData = {
        sessionId: this.currentSession.sessionId,
        lastSync: Date.now(),
        deviceType: this.detectDeviceType(),
        messageCount: this.currentSession.messages.length
      };

      // Store sync marker
      localStorage.setItem('emcog_sync_marker', JSON.stringify(syncData));

      // 🔥 NEW: Actually sync to server if we have pending changes
      if (this.pendingServerSync && this.webSocketSend) {
        this.syncToServer();
        this.pendingServerSync = false;
      }

    } catch (error) {
      console.error('Session sync failed:', error);
    }
  }

  /**
   * Detect device type for session metadata
   */
  private detectDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.screen.width;
    
    if (/mobile|android|iphone/.test(userAgent) || screenWidth < 768) {
      return 'mobile';
    } else if (/tablet|ipad/.test(userAgent) || (screenWidth >= 768 && screenWidth < 1024)) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    return `session_${timestamp}_${random}`;
  }

  /**
   * Check if session is still valid
   */
  private isSessionValid(session: ConversationSession): boolean {
    const age = Date.now() - session.metadata.lastActivity;
    return age < this.sessionTimeout;
  }

  /**
   * Get active session for user
   */
  private getActiveSession(userId: string): ConversationSession | null {
    try {
      const currentSessionData = localStorage.getItem(this.currentSessionKey);
      if (currentSessionData) {
        const session = JSON.parse(currentSessionData) as ConversationSession;
        if (session.userId === userId && this.isSessionValid(session)) {
          return session;
        }
      }
      return null;
    } catch (error) {
      console.error('Failed to get active session:', error);
      return null;
    }
  }

  /**
   * Save session to session list
   */
  private saveToSessionList(session: ConversationSession): void {
    try {
      const sessions = this.getAllSessions();
      const existingIndex = sessions.findIndex(s => s.sessionId === session.sessionId);
      
      if (existingIndex >= 0) {
        sessions[existingIndex] = session;
      } else {
        sessions.push(session);
      }

      // Keep only recent sessions
      const recentSessions = sessions
        .sort((a, b) => b.metadata.lastActivity - a.metadata.lastActivity)
        .slice(0, this.maxSessions);

      localStorage.setItem(this.storageKey, JSON.stringify(recentSessions));
    } catch (error) {
      console.error('Failed to save to session list:', error);
    }
  }

  /**
   * Get all sessions from storage
   */
  private getAllSessions(): ConversationSession[] {
    try {
      const sessionsData = localStorage.getItem(this.storageKey);
      return sessionsData ? JSON.parse(sessionsData) : [];
    } catch (error) {
      console.error('Failed to get all sessions:', error);
      return [];
    }
  }

  /**
   * Start auto-save interval
   */
  private startAutoSave(): void {
    this.autoSaveInterval = window.setInterval(() => {
      if (this.currentSession) {
        this.saveCurrentSession();
      }
    }, 30000); // Save every 30 seconds
  }

  /**
   * Start cross-device sync interval
   */
  private startCrossDeviceSync(): void {
    this.syncInterval = window.setInterval(() => {
      this.syncSessions();
    }, 60000); // Sync every minute
  }

  /**
   * Check for updates from other devices
   */
  private checkForCrossDeviceUpdates(): void {
    // Implementation would depend on backend sync mechanism
    // For now, this is a placeholder for future enhancement
    console.log('🔄 Checking for cross-device session updates...');
  }

  /**
   * Clean up old sessions
   */
  private cleanupOldSessions(): void {
    try {
      const sessions = this.getAllSessions();
      const validSessions = sessions.filter(session => this.isSessionValid(session));
      
      if (validSessions.length !== sessions.length) {
        localStorage.setItem(this.storageKey, JSON.stringify(validSessions));
        console.log(`🧹 Cleaned up ${sessions.length - validSessions.length} expired sessions`);
      }
    } catch (error) {
      console.error('Failed to cleanup old sessions:', error);
    }
  }

  /**
   * Delete a session by ID
   * Also syncs deletion to server for cross-device consistency
   */
  public deleteSession(sessionId: string): boolean {
    try {
      const sessions = this.getAllSessions();
      const filteredSessions = sessions.filter(s => s.sessionId !== sessionId);

      if (filteredSessions.length === sessions.length) {
        console.warn('Session not found for deletion:', sessionId);
        return false;
      }

      // 🔥 Track this session as deleted to prevent re-merge from server
      this.deletedSessionIds.add(sessionId);
      this.saveDeletedSessions();

      localStorage.setItem(this.storageKey, JSON.stringify(filteredSessions));

      // If we deleted the current session, clear current session
      if (this.currentSession?.sessionId === sessionId) {
        this.currentSession = null;
        localStorage.removeItem(this.currentSessionKey);
      }

      // Sync deletion to server for cross-device consistency
      if (this.webSocketSend) {
        this.webSocketSend({
          type: 'delete_session',
          sessionId: sessionId,
          userId: 'default'
        });
        console.log('📡 Session deletion synced to server:', sessionId);
      }

      console.log('🗑️ Session deleted:', sessionId);
      return true;
    } catch (error) {
      console.error('Failed to delete session:', error);
      return false;
    }
  }

  /**
   * Export session data
   */
  public exportSession(sessionId?: string): string {
    const targetSession = sessionId 
      ? this.getAllSessions().find(s => s.sessionId === sessionId)
      : this.currentSession;

    if (!targetSession) {
      throw new Error('Session not found for export');
    }

    return JSON.stringify(targetSession, null, 2);
  }

  /**
   * Import session data
   */
  public importSession(sessionData: string): ConversationSession {
    try {
      const session = JSON.parse(sessionData) as ConversationSession;
      
      // Validate session structure
      if (!session.sessionId || !session.userId || !Array.isArray(session.messages)) {
        throw new Error('Invalid session data structure');
      }

      // Update session ID to avoid conflicts
      session.sessionId = this.generateSessionId();
      session.metadata.lastActivity = Date.now();

      this.saveToSessionList(session);
      return session;
    } catch (error) {
      console.error('Failed to import session:', error);
      throw new Error('Failed to import session data');
    }
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
    
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Final save
    if (this.currentSession) {
      this.saveCurrentSession();
    }
  }
}